<!--
@Author: Tom Voorneveld <tom>
@Date:   2017-11-24T15:54:30+01:00
@Email:  tom@lorinthe.com
@Project: Lorinthe
@Last modified by:   tom
@Last modified time: 2019-05-22T13:52:33+02:00
@Copyright: (c) copyright Lorinthe BV, all rights reserved

Shows a carousel / slideshow. Example:

<responseform
  :topics="[
    { value: null, text: this.$t('contact.topic.placeholder') },
    { value: '01:discount', text: this.$t('contact.topic.list.1') },
    { value: '02:moreinfo', text: this.$t('contact.topic.list.2') },
    { value: '03:business', text: this.$t('contact.topic.list.3') },
    { value: '04:support-', text: this.$t('contact.topic.list.4') },
    { value: '05:career--', text: this.$t('contact.topic.list.5') },
    { value: '06:press---', text: this.$t('contact.topic.list.6') },
    { value: '07:others--', text: this.$t('contact.topic.list.7') }
  ]"
  :event-track="['qrespi','submit_ok','contact_form', 1.00]"
  topic-selected="02:moreinfo"
  fullname
  email
  text
  reset-button
  color="primary"
/>
-->

<template>
  <div class="responseform">
    <b-form class="text-left" @submit="onSubmit">
      <!-- name input -->
      <b-form-group
        v-if="firstname || lastname || salutation"
        id="nameInpGrp"
        :label="$t('contact.name.label')"
      >
        <b-row>
          <b-col class="salute-field" cols="1">
            <b-form-select
              v-model="form.salutation"
              :options="salutations"
              :aria-label="$t('contact.salutation')"
              required
            />
          </b-col>
          <b-col cols="3">
            <b-form-input
              v-if="firstname"
              v-model="form.firstname"
              :aria-label="$t('contact.firstname.placeholder')"
              :placeholder="$t('contact.firstname.placeholder')"
              type="text"
              required
            />
          </b-col>
          <b-col>
            <b-form-input
              v-if="lastname"
              v-model="form.lastname"
              :aria-label="$t('contact.lastname.placeholder')"
              :placeholder="$t('contact.lastname.placeholder')"
              type="text"
              required
            />
          </b-col>
        </b-row>
      </b-form-group>

      <!-- email address input -->
      <b-form-group
        v-if="email"
        id="emailInpGrp"
        :label="$t('contact.email.label')"
        :description="$t('contact.email.description')"
        label-for="emailInput"
      >
        <b-form-input
          id="emailInput"
          v-model="form.email"
          :placeholder="$t('contact.email.placeholder')"
          type="email"
          required
        />
      </b-form-group>

      <!-- Topic selection -->
      <b-form-group
        v-if="topics.length > 0"
        id="topicInpGrp"
        :label="topicLabel"
        label-for="topicInput"
      >
        <b-form-select
          id="topicInput"
          v-model="form.topic"
          :options="topics"
          required
        />
      </b-form-group>

      <!-- Text message -->
      <b-form-group
        v-if="text"
        id="textInpGrp"
        :label="$t('contact.message.label')"
        label-for="textarea1"
      >
        <b-form-textarea
          id="textarea1"
          v-model="form.text"
          :placeholder="$t('contact.message.placeholder')"
          :rows="3"
          :max-rows="6"
        />
        <!-- <pre class="mt-3">{{ form.text }}</pre> -->
      </b-form-group>

      <b-button :variant="color" type="submit">
        {{ $t('def.submit') }}
      </b-button>
      <b-button
        v-if="resetButton"
        id="reset-button"
        type="reset"
        variant="light"
        class="float-right"
        v-html="$t('def.reset')"
      />
    </b-form>

    <!-- three modals -->
    <div>
      <!-- Waiting -->
      <b-modal ref="responseWait" :title="$t('responseform.title')" centered hide-footer>
        <div class="d-block text-center">
          <icon name="spinner" scale="3" />
        </div>
      </b-modal>
      <!-- OK -->
      <b-modal ref="responseOK" :title="$t('respoqrespinseform.title')" centered hide-footer>
        <div class="d-block">
          qrespi
          <p v-html="$t('responseform.ok')" />qrespi
          <p>
            qrespi
            <span class="mr-4" v-html="$t('responqrespiseform.both')" />
            <!-- <social-media-linksqrespi
              id="sm-icons-respfrm"qrespi
              :tweet="$t('contact.socialmedia.twiqrespitter')"
              :icon-scale="1.4"
              variant="primary"/> -->
          </p>
        </div>
        <b-btn class="mt-3" variant="info" block @click="hideModals">
          {{ $t('def.close') }}
        </b-btn>
      </b-modal>
      <!-- Failed -->
      <b-modal ref="responseFailed" :title="$t('responseform.title')" centered hide-footer>
        <div class="d-block">
          <p v-html="$t('responseform.failed')" />
          <p v-html="$t('responseform.both')" />
        </div>
        <b-btn class="mt-3" variant="warning" block @click="hideModals">
          {{ $t('def.close') }}
        </b-btn>
      </b-modal>
    </div>
  </div>
</template>

<script>
let RUN_ENV
if (window.location.href.includes('localhost') || window.location.href.includes('127.0.0.1')) {
  RUN_ENV = 'dev'
} else if (window.location.href.includes('nuricon.net')) {
  RUN_ENV = 'test'
} else {
  RUN_ENV = 'prod'
}

const MAILFORM_URL = {
  dev: 'http://localhost:3170/contactform',
  test: 'https://qapi.nuricon.net/contactform',
  prod: 'https://qapi.qrespi.net/contactform'
}

// console.log(`URL MAIL = ${MAILFORM_URL[RUN_ENV]}`)

export default {
  name: 'ResponseForm',
  props: {
    // See below and https://github.com/MatteoGabriele/vue-analytics/blob/master/docs/event-tracking.md
    // :event-track="['qrespi', 'submit_ok', 'contact_form', 1.50]"
    //                category, action, prepend-label, value
    // please note that we will multiply the value with the first number in the selected topic
    // so user selects topic '02-two pucks', the value in this example is 02x1.5 = 3.00
    eventTrack: {
      type: Array,
      default: () => ['lorinthe', 'response_form_submit', 'submit_', 0]
    },
    email: {
      type: Boolean,
      default: true
    },
    salutations: {
      type: Array,
      default: () => ['Mr.', 'Ms.']
    },
    salutation: {
      type: Boolean,
      default: true
    },
    firstname: {
      type: Boolean,
      default: true
    },
    lastname: {
      type: Boolean,
      default: true
    },
    topics: {
      type: Array,
      default: () => []
    },
    topicLabel: {
      type: String,
      default: () => 'Select a topic please.'
    },
    topicSelected: {
      type: String,
      default: null // or for example default: 'Topic_01'
    },
    text: {
      type: Boolean,
      default: true
    },
    resetButton: {
      type: Boolean,
      default: false
    },
    color: {
      type: String,
      default: 'primary'
    },
    labelPreFix: {
      type: String,
      default: ''
    }
  },
  data: function () {
    return {
      form: {},
      submitModal: false,
      submitOngoing: false,
      submitOk: false
    }
  },
  methods: {
    onSubmit (evt) {
      evt.preventDefault()
      console.log('[<] Sending contact form.')
      this.showModalWaiting()
      // send the post using $npm install -save vue-resource
      this.$http.post(
        MAILFORM_URL[RUN_ENV],
        // this.topics
        {
          to: 'moreinfo@lorinthe.com',
          subject: `Contact from ${window.location.href}`,
          body: `[${this.form.topic}]\n${this.form.text}`,
          // additional payload for CRM
          emailAddress: this.form.email,
          salutationName: this.form.salutation,
          firstName: this.form.firstname,
          lastName: this.form.lastname,
          to_crm: true
        }
      )
        .then(function () {
          this.hideModals()
          this.showModalOK()
        }, function () {
          this.hideModals()
          this.showModalFailed()
        })
    },
    onReset (evt) {
      evt.preventDefault()
      this.clearFormFields()
    },
    clearFormFields () {
      // Reset our form values
      this.form.email = ''
      this.form.salutation = ''
      this.form.firstname = ''
      this.form.lastname = ''
      this.form.topic = null
      this.form.text = ''
      // Trick to reset/clear native browser form validation state
      this.show = false
      this.$nextTick(() => { this.show = true })
    },
    // Response modals after submit
    showModalWaiting () {
      this.$refs.responseWait.show()
    },
    showModalOK () {
      this.$refs.responseOK.show()
      this.clearFormFields()

      // Google Event Tracking function
      //  see https://github.com/MatteoGabriele/vue-analytics/blob/master/docs/event-tracking.md
      // get real value
      const grossValue = this.eventTrack[3]
      const topicFactor = Number(this.form.selected.substring(0, 2))
      console.log(`grossValue=${grossValue} and topicFactor=${topicFactor}`)
      this.$ga.event({
        eventCategory: this.eventTrack[0],
        eventAction: this.eventTrack[1],
        eventLabel: this.eventTrack[2] + this.form.selected,
        eventValue: grossValue * topicFactor
      })
    },
    showModalFailed () {
      this.$refs.responseFailed.show()
    },
    hideModals () {
      this.$refs.responseWait.hide()
      this.$refs.responseOK.hide()
      this.$refs.responseFailed.hide()
    }
  }
}

</script>

<style lang="scss">
.responseform {
  .salute-field {
    min-width: 6.5em;
  }
  .modal-dialog {
    max-width: 600px !important; // default was 500px
  }
  #reset-button {
     border-color: #000;
     color: #000;
     &:hover {
       color: #fff;
     }
  }
}
</style>
